import { serverLogoutUser } from '@/hooks/useAuth';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import store, { useStoreState } from '@/stores/store';
import { localLogoutUser } from '@/api/services/user';
import { navigate } from '@/navigations/RootNavigation';
import { useDialog } from '@/hooks/useDialog';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { isPatientApp } from '@/config/constants';
import { PrimaryButton } from '@/components/Button';
import { styles } from './styles';

export type Session = {
  accessToken: string;
  app: string;
  deviceModel: string;
  created: string;
};

type SessionsListProps = {
  sessions?: Session[];
  usePadding?: boolean;
};

export const SessionsList = ({
  sessions,
  usePadding = false,
}: SessionsListProps) => {
  const [activeSessions, setActiveSessions] = useState<Session[]>(
    sessions ?? [],
  );
  const signedInUser = useStoreState((store) => store.signedInUser);
  const { removeAllDialogs } = useDialog();

  useEffect(() => {
    setActiveSessions(sessions ?? []);
  }, [sessions]);

  const removeSession = async (accessToken: Session['accessToken']) => {
    await serverLogoutUser({ jwt: accessToken });
    setActiveSessions((prevSessions) =>
      prevSessions.filter((session) => session.accessToken !== accessToken),
    );

    if (accessToken === signedInUser?.accessToken) {
      localLogoutUser();
      store.getActions().setStayLoggedIn(false);
      !store.getState().isAuthenticated && navigate('Login');
      removeAllDialogs();
    }
  };

  if (activeSessions.length === 0) {
    return (
      <Text style={styles.baseText}>
        Sie sind derzeit auf keinem Gerät angemeldet.
      </Text>
    );
  }

  return (
    <View style={styles.listContainer}>
      {activeSessions.map((session, index) => {
        const isLast = index === activeSessions.length - 1;
        return (
          <View key={session.accessToken}>
            <SessionItem
              session={session}
              onSessionRemove={removeSession}
              isCurrentSession={
                signedInUser?.accessToken === session.accessToken
              }
              usePadding={usePadding}
            />
            {!isLast && <View style={styles.divider} />}
          </View>
        );
      })}
    </View>
  );
};

const SessionItem = ({
  session,
  onSessionRemove,
  isCurrentSession,
  usePadding = false,
}: {
  session: Session;
  isCurrentSession: boolean;
  onSessionRemove: (accessToken: Session['accessToken']) => void;
  usePadding?: boolean;
}) => {
  const formattedDate = moment(session.created).format('DD-MM-YYYY HH:mm');
  const appCapitalized =
    session.app.charAt(0).toUpperCase() + session.app.slice(1);
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);

  return (
    <View style={[styles.row, usePadding && styles.rowWithPadding]}>
      <View>
        <Text style={[styles.semiboldText, { marginBottom: 6 }]}>
          {appCapitalized} {!!isCurrentSession && '(dieses Gerät)'}
        </Text>
        <Text style={{ fontSize: 14, fontWeight: '400' }}>
          {'zuletzt aktiv am '}
          {formattedDate}
        </Text>
      </View>
      <Button
        onPress={() => {
          if (isCurrentSession) {
            setIsLogoutModalVisible(true);
          } else {
            onSessionRemove(session.accessToken);
          }
        }}
        label="Abmelden"
        look="tertiary"
      />
      <Modal
        isVisible={isLogoutModalVisible}
        title="Abmelden"
        confirmActionButton={
          isPatientApp ? (
            <PrimaryButton
              label="Abmelden"
              look="danger"
              onPress={() => {
                onSessionRemove(session.accessToken);
                setIsLogoutModalVisible(false);
              }}
            />
          ) : (
            <Button
              label="Abmelden"
              look="danger"
              onPress={() => {
                onSessionRemove(session.accessToken);
                setIsLogoutModalVisible(false);
              }}
            />
          )
        }
        cancelActionButton={
          isPatientApp ? (
            <PrimaryButton
              label="Abbrechen"
              look="neutral"
              onPress={() => setIsLogoutModalVisible(false)}
            />
          ) : (
            <Button
              label="Abbrechen"
              look="secondary"
              onPress={() => setIsLogoutModalVisible(false)}
            />
          )
        }
        onClose={() => setIsLogoutModalVisible(false)}
      >
        <Text style={{ fontSize: 20 }}>
          Möchten Sie sich wirklich abmelden?
        </Text>
      </Modal>
    </View>
  );
};
